import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import { Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import AddIcon from '@mui/icons-material/Add'
import MergeTypeIcon from '@mui/icons-material/MergeType'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import { getCustomerList } from 'app/redux/store/features/customers'
import CustomerGridFilter from './components/CustomerGridFilter'
import CustomerGridData from './components/CustomerGridData'
import CustomerMergeForm from './components/CustomerMergeForm'
import { Capitalize } from 'app/utils/components/StringUtils/stringUtils'
import { useSnackbar } from 'notistack'
import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog'
import Div from '@jumbo/shared/Div'

const CustomersGrid = () => {
    const { state } = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { showDialog } = useJumboDialog()
    const { customers, loading, error } = useSelector((state) => state.customers)
    const [filter, setFilter] = useState({
        firstName: '',
        lastName: '',
        familyName: '',
        email: '',
        phoneNumber: '',
        sort: 'FirstName',
        isDescending: false,
    })
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const pageSize = 25

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
    }, [loading])

    useEffect(() => {
        const shouldFetch = !state?.navigateFrom || !customers.data?.length
        if (shouldFetch) {
            setApiCallInProgress(true)
            dispatch(getCustomerList({ ...filter, skip: 0, take: pageSize }))
        }
    }, [state?.navigateFrom, dispatch])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    function handleSuccess(message) {
        enqueueSnackbar(message, {
            variant: 'success',
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
            },
        })
    }

    function init(initialValue) {
        return {
            gridFilterVisible: initialValue.gridFilterVisible,
            filterIconColor: initialValue.filterIconColor,
        }
    }

    function userReducer(state, action) {
        switch (action.type) {
            case 'set-grid-filter':
                return {
                    ...state,
                    gridFilterVisible: action.payload,
                }
            case 'set-filter-icon-color':
                return {
                    ...state,
                    filterIconColor: action.payload,
                }
            default:
                return state
        }
    }

    const [customerGrid, setCustomerGrid] = React.useReducer(
        userReducer,
        {
            gridFilterVisible: false,
            filterIconColor: false,
        },
        init
    )

    const applyFilter = useMutation({
        mutationFn: (currentfilter) => {
            dispatch(getCustomerList({ ...currentfilter, skip: 0, take: pageSize }, true))
            setFilter(currentfilter)
        },
    })

    const onPageChange = (pageNumber) => {
        const currentFilter = { ...filter, skip: pageSize * pageNumber, take: pageSize }
        dispatch(getCustomerList(currentFilter))
        setFilter(currentFilter)
    }

    const onColumnSort = (sortOrder) => {
        const columnName = Capitalize(sortOrder?.[0]?.columnName || '')
        const currentFilter = {
            ...filter,
            sort: columnName,
            isDescending: sortOrder[0].direction !== 'asc',
            take: pageSize,
        }
        dispatch(getCustomerList(currentFilter))
        setFilter(currentFilter)
    }

    const onRefresh = () => {
        setApiCallInProgress(true)
        dispatch(getCustomerList({ ...filter, skip: 0, take: pageSize }, true))
        setCurrentPage(0)
    }

    const setGridFilter = React.useCallback(
        (value) => {
            setCustomerGrid({ type: 'set-grid-filter', payload: value })
        },
        [setCustomerGrid]
    )

    const onAddCustomer = () => {
        navigate('/registrations/customers/addcustomer')
    }

    const onMergeCustomers = () => {
        showDialog({
            maxWidth: false,
            fullWidth: true,
            sx: {
                '& .MuiPaper-root': {
                    borderRadius: 2,
                    maxWidth: 650,
                    width: '100%',
                },
            },
            title: (
                <Div
                    sx={{
                        textAlign: 'center',
                        height: 48,
                        pt: 1.5,
                        bgcolor: 'primary.main',
                        borderRadius: 1,
                        color: 'common.white',
                    }}>
                    Merge Customers and Families
                </Div>
            ),
            content: <CustomerMergeForm onRefresh={onRefresh} handleError={handleError} handleSuccess={handleSuccess} />,
            disableEscapeKeyDown: true,
            disableDefaultClose: true,
        })
    }

    const setFilterIconColor = React.useCallback(
        (value) => {
            setCustomerGrid({ type: 'set-filter-icon-color', payload: value })
        },
        [setCustomerGrid]
    )

    return (
        <JumboCardQuick
            noWrapper
            title={
                <Typography color={'common.white'} variant={'h3'}>
                    <AutorenewRoundedIcon
                        onClick={onRefresh}
                        fontSize={'large'}
                        sx={{
                            mb: -1.2,
                            ml: -2,
                            mr: 0.5,
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                    />
                    Customer List
                </Typography>
            }
            sx={{ bgcolor: 'primary.main' }}
            headerSx={{
                height: 40,
            }}
            action={
                <Stack direction={'row'} spacing={4} sx={{ mt: -2 }}>
                    <AddIcon
                        fontSize={'large'}
                        variant={'contained'}
                        sx={{
                            pt: 0,
                            pb: 0,
                            color: 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={onAddCustomer}
                    />
                    <MergeTypeIcon
                        fontSize={'large'}
                        sx={{
                            pt: 0,
                            pb: 0,
                            color: 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={onMergeCustomers}
                    />
                    <FilterAltIcon
                        fontSize='large'
                        sx={{
                            mt: -1.8,
                            color:
                                customerGrid.filterIconColor && !customerGrid.gridFilterVisible
                                    ? 'success.main'
                                    : 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={() => setGridFilter(!customerGrid.gridFilterVisible)}
                    />
                </Stack>
            }>
            <Grid>
                <CustomerGridFilter
                    mutation={applyFilter}
                    display={customerGrid.gridFilterVisible ? '' : 'none'}
                    filterIconColor={(data) => setFilterIconColor(data)}
                />
            </Grid>
            <Grid>
                <CustomerGridData
                    onRefresh={onRefresh}
                    onPageChange={onPageChange}
                    onColumnSort={onColumnSort}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    handleError={handleError}
                />
            </Grid>
        </JumboCardQuick>
    )
}
export default CustomersGrid
